"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import Script from "next/script";

const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID ?? "";

export default function Analytics() {
  const pathname = usePathname();

  useEffect(() => {
    if (!GTM_ID) return;
    if (typeof window === "undefined") return;
    if (!/(?:^|; )otr_consent=accepted(?:;|$)/.test(document.cookie)) return;

    let timeout: number | undefined;
    let attempts = 0;
    const sendPageView = () => {
      window.__otrLoadMarketingTags?.();
      if (window.__otrTrackPageView) {
        window.__otrTrackPageView();
        return;
      }
      attempts += 1;
      if (attempts < 5) {
        timeout = window.setTimeout(sendPageView, 50);
      }
    };

    sendPageView();
    return () => {
      if (timeout !== undefined) {
        window.clearTimeout(timeout);
      }
    };
  }, [pathname]);

  if (!GTM_ID) return null;

  return (
    <Script id="marketing-tags-init" strategy="afterInteractive">
      {`
        (function(){
        var gtmId = ${JSON.stringify(GTM_ID)};
        window.dataLayer = window.dataLayer || [];
        window.gtag = window.gtag || function(){window.dataLayer.push(arguments);};

        var hasAnalyticsConsent = /(?:^|; )otr_consent=accepted(?:;|$)/.test(document.cookie);

        window.__otrMarketingTagsLoaded = window.__otrMarketingTagsLoaded || {};

        window.__otrLoadMarketingTags = function() {
          if (window.__otrMarketingTagsLoaded.gtm || window.__otrMarketingTagsLoaded.gtmRequested) return;
          window.__otrMarketingTagsLoaded.gtmRequested = true;
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;j.onload=function(){
          window.__otrMarketingTagsLoaded.gtm = true;};j.onerror=function(){
          window.__otrMarketingTagsLoaded.gtmRequested = false;};f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer',gtmId);
        };

        window.__otrTrackPageView = function() {
          window.dataLayer.push({
            event: 'otr_page_view',
            page_location: window.location.href,
            page_path: window.location.pathname + window.location.search,
            page_title: document.title,
          });
        };

        if (hasAnalyticsConsent) {
          window.__otrLoadMarketingTags();
        }

        })();
      `}
    </Script>
  );
}

function getDataLayer() {
  if (typeof window === "undefined") return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

function getGtag() {
  const dataLayer = getDataLayer();
  if (!dataLayer) return null;
  window.gtag = window.gtag || ((...args: unknown[]) => {
    dataLayer.push(args);
  });
  return window.gtag;
}

// Grant consent — called when user accepts cookies
export function grantConsent({ sendPageView = true } = {}) {
  const gtag = getGtag();
  if (!gtag) return;

  gtag("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });

  getDataLayer()?.push({ event: "otr_consent_granted" });
  window.__otrLoadMarketingTags?.();

  if (sendPageView) {
    window.__otrTrackPageView?.();
  }
}

// Push events to GTM dataLayer for tag triggers
export function trackEvent(name: string, params?: Record<string, string | number>) {
  getDataLayer()?.push({ event: name, ...params });
}
